import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { hasBetterNotesAccess } from '../../lib/featureFlags'
import { POST_PAYMENT_TOUR_STEPS } from '../../config/postPaymentTour'
import { usePostPaymentTour } from '../../hooks/usePostPaymentTour'
import { useSpotlightRect } from '../../hooks/useSpotlightRect'
import TourSpotlight from './TourSpotlight'
import TourCard from './TourCard'
import TourFinishAnimation from './TourFinishAnimation'

// Smooth-scroll budget. The browser fires no event when smooth scroll
// completes, so we just wait. 320ms covers the typical Chrome/Safari
// scroll animation comfortably without making the tour feel laggy.
const SCROLL_SETTLE_MS = 320
// Max time to wait for a target to appear after a route swap (covers
// Suspense fallback + lazy chunk fetch on slow networks). After this
// the tour proceeds with whatever rect is available.
const TARGET_LOOKUP_TIMEOUT_MS = 1200

function findVisibleTourTarget(selector: string): HTMLElement | null {
  const els = Array.from(document.querySelectorAll(selector) as NodeListOf<HTMLElement>)
  for (const el of els) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return null
}

/**
 * PostPaymentTour — once-per-device guided walkthrough triggered after
 * a successful Stripe checkout (or via the Replay tile in /tutorials).
 *
 * Walks the user through the two paid-only modules end-to-end:
 *   - 5 BetterNotes steps (nav highlight → Library → Upcoming → Past → 4 Settings spotlights)
 *   - 3 Integrations steps (Telegram → Slack → REST API)
 *   - + welcome and finish bookends
 *
 * Differs from FirstLoginTour in one important way: the tour spans 4
 * different routes, so the component listens to the current pathname
 * via `useLocation` and navigates the user between routes when a
 * step's `navigatesNext` flag is set. If the user uses the browser
 * Back button mid-tour, the tour quietly hides until they return to
 * the matching route — index is preserved.
 *
 * Triggers:
 *   - Stripe checkout success → `markPostPaymentTourPending()` + redirect
 *     to `/dashboard?tour=post_payment` (handled in PaymentSuccess.tsx)
 *   - Replay tile in `/tutorials` → `reset()` + same redirect
 *   - Direct URL `?tour=post_payment` (force open, bypasses seen flag)
 *
 * Keyboard: ←/→ = nav, Enter = next. No Esc / X / click-outside —
 * matches FirstLoginTour's "tour is mandatory" stance per Mustapha
 * 2026-04-27.
 */
export default function PostPaymentTour() {
  const { t } = useTranslation('tour')
  const { profile } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { seen, pending, markSeen, consumePending } = usePostPaymentTour()

  const urlForce = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('tour') === 'post_payment'
  }, [location.search])

  // Gate: paid users only (the tour is paid-only by definition), AND
  // never seen before. `seen` is the master gate — it suppresses BOTH
  // the pending and urlForce paths so the tour never re-fires on legit
  // post-paid events where the user already knows the product.
  //
  // Cases that do NOT re-fire the tour (Mustapha 2026-04-29):
  //   - Subscription renewal (Stripe webhook backend, no client trigger)
  //   - Tier upgrade (Shop.confirmUpgrade does not write lastPurchaseType)
  //   - Tier downgrade (same path as upgrade)
  //   - Credit pack purchase (lastPurchaseType='pack' ≠ 'subscription')
  //   - Re-subscribe after cancel (PaymentSuccess routes here with
  //     pending+urlForce, but seen=true now blocks both)
  //   - Direct ?tour=post_payment URL share / bookmark after seen
  //   - Stale pending from a previous aborted redirect
  //
  // The /tutorials Replay tile bypasses this gate by calling
  // `resetPostPaymentTour()` BEFORE redirecting (Tutorials.tsx:338),
  // which flips seen back to false in localStorage so the next mount
  // re-fires the tour.
  const isPaid = hasBetterNotesAccess(profile)
  const shouldOpen = isPaid && !seen && (urlForce || pending)

  const steps = POST_PAYMENT_TOUR_STEPS

  const [entered, setEntered] = useState(false)
  const [index, setIndex] = useState(0)
  // When a step lives on a different route, we delay committing the new
  // index until the router has actually swapped to that route. Otherwise
  // AnimatePresence + same-tick navigate() + setIndex() can strand the
  // fullscreen "transition" card behind the exiting anchored card on the
  // previous route (cf. step 10 transition_integrations bug, 2026-04-28).
  const [routePendingIndex, setRoutePendingIndex] = useState<number | null>(null)
  const [isFinishing, setIsFinishing] = useState(false)
  // Tracks which step has finished its scrollIntoView + settle delay.
  // Only when this matches the current step do we expose `activeSelector`
  // and render the TourCard, so the spotlight never tracks a stale
  // off-screen rect or appears for half a frame before the scroll lands.
  const [settledStepId, setSettledStepId] = useState<string | null>(null)

  // Delay 800ms entrance so the dashboard / route transition settles.
  useEffect(() => {
    if (!shouldOpen) return
    const id = window.setTimeout(() => setEntered(true), 800)
    return () => window.clearTimeout(id)
  }, [shouldOpen])

  // Consume the pending flag whenever we either fire the tour OR observe
  // that seen=true (which means we'll never fire it). This keeps
  // localStorage clean — a stale pending must not linger past the first
  // mount that observes seen=true (cf. re-subscribe after cancel: the
  // PaymentSuccess flow writes pending=true, but seen=true now blocks
  // shouldOpen, so we cleanup pending here instead of leaving it set).
  useEffect(() => {
    if (pending && (seen || shouldOpen)) consumePending()
  }, [pending, seen, shouldOpen, consumePending])

  // Mark seen the MOMENT the tour decides to render — same defensive
  // pattern as FirstLoginTour: if the user navigates away mid-tour,
  // returning shouldn't re-prompt them. The URL-force replay path
  // skips the markSeen so QA can re-trigger.
  useEffect(() => {
    if (!shouldOpen || urlForce || seen) return
    markSeen()
  }, [shouldOpen, urlForce, seen, markSeen])

  const open = shouldOpen && entered

  // Mustapha 2026-04-28: REMOVED body scroll lock. The lock used to be
  // here to prevent the user from scrolling away from the spotlight,
  // but `overflow: hidden` on <body> ALSO blocks `scrollIntoView()` —
  // which is exactly how the new step orchestration brings sections 8
  // and 10-13 (briefing/EOD/weekly/timezone, all near the bottom of
  // /integrations) into view before showing the card. Without scroll,
  // those steps appeared "frozen" or rendered off-screen for ~1s
  // until the lookup deadline elapsed and TourCard fell back to
  // fullscreen. The fixed-position overlay backdrop already prevents
  // pointer events on the underlying page, so users can't interact
  // with anything outside the tour even when scroll is technically
  // available.

  // If a route swap is pending and the router has just landed on the
  // target route, surface the next step's index *for the render path*
  // (without setting state yet — the dedicated effect below commits it
  // a microtask later). This makes the fullscreen "transition" step
  // render immediately on the new route, instead of waiting for an
  // exit animation on the previous route to finish.
  const pendingStep = routePendingIndex !== null ? steps[routePendingIndex] : undefined
  const activeIndex =
    pendingStep && location.pathname === pendingStep.routePath
      ? routePendingIndex!
      : index
  const currentStep = steps[activeIndex]
  const isOnRoute = currentStep ? location.pathname === currentStep.routePath : false
  const isCurrentSpotlight = currentStep?.mode === 'spotlight'

  // Commit the pending route-bound index to real state once the router
  // settles on the target pathname. Defers via queueMicrotask to satisfy
  // react-hooks/set-state-in-effect.
  useEffect(() => {
    if (routePendingIndex === null) return
    const step = steps[routePendingIndex]
    if (!step || location.pathname !== step.routePath) return
    queueMicrotask(() => {
      setIndex(routePendingIndex)
      setRoutePendingIndex(null)
    })
  }, [routePendingIndex, location.pathname, steps])

  // Per-step scroll-into-view orchestration. When a spotlight step
  // becomes active on the right route, poll for the target element
  // (covers lazy-loaded pages where the anchor mounts after the
  // tour re-renders), scroll it into the viewport center, wait for
  // the smooth scroll to settle, then mark this step as ready. Only
  // then do we expose the selector to useSpotlightRect + render the
  // card, so steps 8 and 10-13 (which sit far down on Settings /
  // Integrations) never land below the fold.
  useEffect(() => {
    // Synchronous setState in an effect body trips
    // react-hooks/set-state-in-effect; queueMicrotask defers the write
    // to the next microtask which satisfies the rule and still arrives
    // in time for the next React render cycle.
    if (!open || !currentStep || !isOnRoute || !isCurrentSpotlight || !currentStep.targetSelector) {
      const fallbackId = currentStep?.id ?? null
      queueMicrotask(() => setSettledStepId(fallbackId))
      return
    }

    let cancelled = false
    let raf = 0
    let settleTimer = 0
    const deadline = performance.now() + TARGET_LOOKUP_TIMEOUT_MS
    const selector = currentStep.targetSelector
    const stepId = currentStep.id

    queueMicrotask(() => {
      if (!cancelled) setSettledStepId(null)
    })

    const waitForTarget = () => {
      if (cancelled) return
      const target = findVisibleTourTarget(selector)

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        settleTimer = window.setTimeout(() => {
          if (!cancelled) setSettledStepId(stepId)
        }, SCROLL_SETTLE_MS)
        return
      }

      if (performance.now() < deadline) {
        raf = window.requestAnimationFrame(waitForTarget)
      } else {
        // Fallback: target never appeared. Show the card anyway —
        // TourCard's null-rect branch falls back to fullscreen, which
        // is still better than a frozen tour.
        setSettledStepId(stepId)
      }
    }

    raf = window.requestAnimationFrame(waitForTarget)

    return () => {
      cancelled = true
      if (raf) window.cancelAnimationFrame(raf)
      if (settleTimer) window.clearTimeout(settleTimer)
    }
  }, [open, isOnRoute, isCurrentSpotlight, currentStep?.id, currentStep?.targetSelector, currentStep])

  // Only spotlight when the user is on the matching route AND the
  // scroll has settled — avoids the spotlight collapsing to (0, 0)
  // during route transitions or tracking a stale rect mid-scroll.
  const activeSelector =
    open && isOnRoute && isCurrentSpotlight && settledStepId === currentStep?.id
      ? currentStep.targetSelector ?? null
      : null

  const rect = useSpotlightRect(activeSelector)

  // Always preserve the `?tour=post_payment` query string when navigating
  // between tour steps. Without it the urlForce gate in shouldOpen flips
  // to false on the next render and the entire tour closes mid-walkthrough
  // (the seen flag was never set on the urlForce path, so we'd lose every
  // gate at once). The finish animation strips the param via a hard
  // assign to /dashboard, so the URL stays clean post-tour.
  const TOUR_QUERY = '?tour=post_payment'

  const goNext = useCallback(() => {
    if (index >= steps.length - 1) {
      markSeen()
      setIsFinishing(true)
      return
    }
    const nextIndex = index + 1
    const nextStep = steps[nextIndex]
    if (nextStep && nextStep.routePath !== location.pathname) {
      // Route swap: stage the pending index, navigate, and let the
      // commit-effect bump real `index` once the pathname matches.
      // activeIndex covers the render in the meantime.
      setRoutePendingIndex(nextIndex)
      navigate(`${nextStep.routePath}${TOUR_QUERY}`)
      return
    }
    setIndex(nextIndex)
  }, [index, steps, navigate, location.pathname, markSeen])

  const goBack = useCallback(() => {
    if (index === 0) return
    const prevIndex = index - 1
    const prevStep = steps[prevIndex]
    if (prevStep && prevStep.routePath !== location.pathname) {
      setRoutePendingIndex(prevIndex)
      navigate(`${prevStep.routePath}${TOUR_QUERY}`)
      return
    }
    setIndex(prevIndex)
  }, [index, steps, navigate, location.pathname])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        goNext()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goBack()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, goNext, goBack])

  // After the user clicks the last step's CTA, swap the tour for the
  // finish animation. Mounted through the same portal so it lives at
  // <body> level above everything; onComplete navigates to the dashboard.
  if (isFinishing) {
    return createPortal(
      <TourFinishAnimation onComplete={() => window.location.assign('/dashboard')} />,
      document.body,
    )
  }

  if (!open || !currentStep) return null

  // If the user is on a route different from the current step's route
  // (e.g. they hit browser Back), hide the tour quietly. Index is
  // preserved — when they come back to the matching route, the tour
  // resumes at the same step.
  if (!isOnRoute) return null

  const isFinal = activeIndex === steps.length - 1
  const isSpotlight = currentStep.mode === 'spotlight'
  // Withhold the spotlight + card render until the scroll has settled,
  // so users never see the card flash at the wrong position before
  // sliding into place. Fullscreen steps render immediately.
  const spotlightReady = !isSpotlight || settledStepId === currentStep.id
  const total = steps.length
  const current = activeIndex + 1

  const stepBase = `post_payment.${currentStep.id}`
  const kickerKeyPath = `${stepBase}.kicker`
  const hasKicker = t(kickerKeyPath, { defaultValue: '' }).length > 0
  const kickerKey = hasKicker ? kickerKeyPath : undefined
  const titleKey = `${stepBase}.title`
  const bodyKey = `${stepBase}.body`
  const primaryLabel = isFinal
    ? t(`${stepBase}.cta_finish`, { defaultValue: t('common.finish') })
    : t(`${stepBase}.cta_next`, { defaultValue: t('common.next') })

  return createPortal(
    <div className="fixed inset-0 z-[70]" role="presentation">
      <div
        data-tour-backdrop="true"
        className="absolute inset-0"
        style={{
          backgroundColor: isSpotlight ? 'transparent' : 'rgba(5, 5, 12, 0.92)',
        }}
      />

      {isSpotlight && spotlightReady ? <TourSpotlight rect={rect} /> : null}

      {/* initial={false} avoids the "wait" trap where the exiting
          anchored card on the previous route delays the entering
          fullscreen card on the next route (cf. step 10 bug). With
          this setting, the new step renders immediately on the new
          route and the old one fades out independently. */}
      <AnimatePresence initial={false}>
        {spotlightReady ? (
          <TourCard
            key={currentStep.id}
            placement={isSpotlight ? 'anchored' : 'fullscreen'}
            rect={rect}
            kickerKey={kickerKey}
            titleKey={titleKey}
            bodyKey={bodyKey}
            current={current}
            total={total}
            stepOfLabel={t('common.step_of', { current, total })}
            primaryLabel={primaryLabel}
            backLabel={t('common.back')}
            showBack={activeIndex > 0}
            isFinal={isFinal}
            onPrimary={goNext}
            onBack={goBack}
          />
        ) : null}
      </AnimatePresence>
    </div>,
    document.body,
  )
}
